import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Hash, Scale, Type, Calculator, Calendar, Users } from "lucide-react";

const gameTypeConfig: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  number: { label: "Pick a Number", icon: Hash, color: "bg-blue-500/10 text-blue-600 border-blue-200" },
  weight: { label: "Guess the Weight", icon: Scale, color: "bg-amber-500/10 text-amber-600 border-amber-200" },
  name: { label: "Guess the Name", icon: Type, color: "bg-emerald-500/10 text-emerald-600 border-emerald-200" },
  quantity: { label: "Guess the Quantity", icon: Calculator, color: "bg-purple-500/10 text-purple-600 border-purple-200" },
};

const fallbackGradients: Record<string, string> = {
  number: "from-blue-500 to-indigo-600",
  weight: "from-amber-400 to-orange-500",
  name: "from-emerald-400 to-teal-500",
  quantity: "from-purple-400 to-fuchsia-500",
};

function getCountdownText(endDate: string) {
  const now = new Date();
  const end = new Date(endDate);
  const diffMs = end.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return "Ending soon";
  if (diffDays === 1) return "Ends tomorrow";
  if (diffDays <= 7) return `Ends in ${diffDays} days`;
  return `Ends ${end.toLocaleDateString()}`;
}

interface GameCardProps {
  game: any;
  entryCount: number;
  onPlay: () => void;
}

export default function GameCard({ game, entryCount, onPlay }: GameCardProps) {
  const config = gameTypeConfig[game.game_type] || gameTypeConfig.number;
  const Icon = config.icon;
  const gradient = fallbackGradients[game.game_type] || fallbackGradients.number;

  return (
    <Card
      className="group overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-elevated hover:-translate-y-1 border-border"
      onClick={onPlay}
    >
      {/* Image / Fallback */}
      <div className="relative aspect-[4/3] overflow-hidden">
        {game.image_url ? (
          <>
            <img
              src={game.image_url}
              alt={game.name}
              className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
            />
          </>
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${gradient} flex items-center justify-center`}>
            <Icon className="h-16 w-16 text-white/30" />
          </div>
        )}

        {/* Price badge overlay */}
        {game.entry_price > 0 && (
          <div className="absolute top-3 right-3">
            <Badge className="bg-white/90 text-foreground backdrop-blur-sm border-0 shadow-sm font-bold">
              £{game.entry_price.toFixed(2)}
            </Badge>
          </div>
        )}
      </div>

      <CardContent className="p-4 space-y-3">
        <div>
          <h3 className="font-semibold text-lg text-foreground line-clamp-1 group-hover:text-primary transition-colors">
            {game.name}
          </h3>
          <Badge variant="outline" className={`mt-1.5 text-[11px] ${config.color}`}>
            <Icon className="h-3 w-3 mr-1" />
            {config.label}
          </Badge>
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {getCountdownText(game.end_date)}
          </span>
          {entryCount > 0 && (
            <span className="flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              {entryCount} of your {entryCount === 1 ? "entry" : "entries"}
            </span>
          )}
        </div>

        <Button className="w-full" size="sm">
          Play Now
        </Button>
      </CardContent>
    </Card>
  );
}
